import * as React from "react";
import { cn } from "lib/utils";

/* ══════════════════════════════════════════════════════════════════════════
   Tabs / SegmentedControl

   Accessible tab strips without adding a dependency: roving tabindex,
   Arrow/Home/End keys, and correct `role="tab"` + `aria-selected` wiring.
   Ad-hoc tab strips across the app were plain buttons with no keyboard model.
   ══════════════════════════════════════════════════════════════════════════ */

export interface TabItem<T extends string = string> {
    value: T;
    label: React.ReactNode;
    icon?: React.ElementType;
    /** Count pill shown after the label */
    count?: number | null;
    disabled?: boolean;
}

export interface TabsProps<T extends string = string> {
    items: TabItem<T>[];
    value: T;
    onChange: (value: T) => void;
    /**
     * `underline` — page-level sections (default)
     * `segmented` — compact switch inside a toolbar or card
     * `pill`      — standalone filter row
     */
    variant?: "underline" | "segmented" | "pill";
    size?: "sm" | "md";
    /** Accessible name for the tab list */
    label?: string;
    className?: string;
}

export function Tabs<T extends string = string>({
    items,
    value,
    onChange,
    variant = "underline",
    size = "md",
    label = "Sections",
    className,
}: TabsProps<T>) {
    const refs = React.useRef<Array<HTMLButtonElement | null>>([]);

    const move = (from: number, delta: number) => {
        const enabled = items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);
        if (!enabled.length) return;
        const pos = enabled.indexOf(from);
        const next = enabled[(pos + delta + enabled.length) % enabled.length];
        refs.current[next]?.focus();
        onChange(items[next].value);
    };

    const onKeyDown = (e: React.KeyboardEvent, index: number) => {
        switch (e.key) {
            case "ArrowRight":
            case "ArrowDown":
                e.preventDefault();
                move(index, 1);
                break;
            case "ArrowLeft":
            case "ArrowUp":
                e.preventDefault();
                move(index, -1);
                break;
            case "Home": {
                e.preventDefault();
                const first = items.findIndex((it) => !it.disabled);
                if (first >= 0) {
                    refs.current[first]?.focus();
                    onChange(items[first].value);
                }
                break;
            }
            case "End": {
                e.preventDefault();
                for (let i = items.length - 1; i >= 0; i--) {
                    if (!items[i].disabled) {
                        refs.current[i]?.focus();
                        onChange(items[i].value);
                        break;
                    }
                }
                break;
            }
            default:
                break;
        }
    };

    const height = size === "sm" ? "h-8 text-meta" : "h-9 text-ui";

    return (
        <div
            role="tablist"
            aria-label={label}
            className={cn(
                "flex items-center min-w-0 overflow-x-auto no-scrollbar",
                variant === "underline" && "gap-1 border-b border-line",
                variant === "segmented" &&
                "gap-0.5 p-0.5 rounded-xl bg-surface-sunken border border-line w-fit max-w-full",
                variant === "pill" && "gap-1.5",
                className
            )}
        >
            {items.map((item, i) => {
                const active = item.value === value;
                const Icon = item.icon;
                return (
                    <button
                        key={item.value}
                        ref={(el) => (refs.current[i] = el)}
                        type="button"
                        role="tab"
                        id={`tab-${item.value}`}
                        aria-selected={active}
                        aria-controls={`tabpanel-${item.value}`}
                        tabIndex={active ? 0 : -1}
                        disabled={item.disabled}
                        onClick={() => onChange(item.value)}
                        onKeyDown={(e) => onKeyDown(e, i)}
                        className={cn(
                            "inline-flex items-center gap-1.5 font-semibold whitespace-nowrap flex-shrink-0",
                            "transition-[color,background-color,border-color,box-shadow] duration-150 ease-ds",
                            "disabled:opacity-40 disabled:cursor-not-allowed focus-ring",
                            height,
                            variant === "underline" &&
                            cn(
                                "px-3 -mb-px border-b-2 rounded-t-lg",
                                active
                                    ? "border-brand text-brand"
                                    : "border-transparent text-content-muted hover:text-content hover:border-line-strong"
                            ),
                            variant === "segmented" &&
                            cn(
                                "px-3 rounded-[10px]",
                                active
                                    ? "bg-surface text-content shadow-xs"
                                    : "text-content-muted hover:text-content"
                            ),
                            variant === "pill" &&
                            cn(
                                "px-3.5 rounded-full border",
                                active
                                    ? "bg-brand-subtle border-brand-border text-brand-on-subtle"
                                    : "bg-surface border-line text-content-secondary hover:bg-surface-hover hover:border-line-strong"
                            )
                        )}
                    >
                        {Icon && <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />}
                        {item.label}
                        {item.count != null && (
                            <span
                                className={cn(
                                    "inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-2xs font-bold tabular-nums",
                                    active ? "bg-brand text-brand-fg" : "bg-surface-sunken text-content-muted"
                                )}
                            >
                                {item.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

/** Body for a tab. Hidden panels stay mounted only if you render them. */
export const TabPanel: React.FC<
    React.HTMLAttributes<HTMLDivElement> & { value: string; active: boolean }
> = ({ value, active, className, children, ...props }) => (
    <div
        role="tabpanel"
        id={`tabpanel-${value}`}
        aria-labelledby={`tab-${value}`}
        hidden={!active}
        tabIndex={0}
        className={cn("focus:outline-none min-w-0", className)}
        {...props}
    >
        {active && children}
    </div>
);

export default Tabs;
